/**
 * ListNotasCreditoCxP.js — Lista de Notas de Crédito CxP
 * Espejo de ListNotasCredito.js (CxC) contra /api/finance/notaCreditoCxP/.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { downloadExcel } from "../../shared/excelService";
import ConfirmModal from "../../shared/ConfirmModal";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

const PAGE_SIZE = 20;

const MOTIVO_LABELS = {
  DEVOLUCION: "notasCredito.motivoDevolucion",
  CORRECCION: "notasCredito.motivoCorreccion",
  DESCUENTO: "notasCredito.motivoDescuento",
  ANULACION: "notasCredito.motivoAnulacion",
  OTRO: "notasCredito.motivoOtro",
};

/** "2025-01-15" → "15/01/2025" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ListNotasCreditoCxP() {
  const { t } = useTranslation();
  const isContador = localStorage.getItem("rol") === "CONTADOR";

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/finance/notaCreditoCxP/?page=${page}&page_size=${PAGE_SIZE}`)
      .then((r) => r.json())
      .then((d) => {
        setItems(d.results ?? []);
        setCount(d.count ?? 0);
        setNext(d.next);
        setPrevious(d.previous);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function confirmarEliminar() {
    const nc = confirmando;
    if (!nc) return;
    setDeletingId(nc.id);
    try {
      const res = await apiFetch(`/api/finance/notaCreditoCxP/${nc.id}/`, {
        method: "DELETE",
      });
      if (res.status !== 204) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || t("notasCredito.errorEliminar"));
      }
      showMsg(true, t("notasCredito.eliminada"));
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeletingId(null);
      setConfirmando(null);
    }
  }

  async function handleExportar() {
    setExportando(true);
    try {
      await downloadExcel("/api/finance/notaCreditoCxP/exportar-excel/", "notas_credito_cxp.xlsx");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setExportando(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const colSpan = isContador ? 6 : 5;

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={invoiceStyles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("notasCredito.tituloCxp")}</span>
      </span>

      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("notasCredito.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>
            {t("notasCredito.administraCxp")}
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportar}
          disabled={exportando}
          className="btn btn-sm"
          style={{
            background: "rgba(74,222,128,0.1)",
            border: "1px solid rgba(74,222,128,0.3)",
            color: "#4ade80",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {exportando ? (
            <i className="fa-solid fa-circle-notch fa-spin" />
          ) : (
            <i className="fa-solid fa-file-export" />
          )}
          {t("common.exportar")}
        </button>
      </div>

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("notasCredito.colNumero")}</th>
                <th>{t("notasCredito.colFactura")}</th>
                <th>{t("notasCredito.colMotivo")}</th>
                <th>{t("common.fecha")}</th>
                <th><div style={{ textAlign: "right" }}>{t("notasCredito.colMonto")}</div></th>
                {isContador && <th className="text-center">{t("common.acciones")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}
                  >
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: 8, color: "#f97316" }}
                    />
                    {t("notasCredito.cargando")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}
                  >
                    <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />
                    {t("notasCredito.sinNotas")}
                  </td>
                </tr>
              ) : (
                items.map((nc) => (
                  <tr key={nc.id}>
                    <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {nc.numero_nota}
                    </td>
                    <td>
                      {nc.factura_cxp?.numero_doc_proveedor ||
                        nc.factura_cxp?.numero_factura ||
                        "—"}
                      {nc.factura_cxp?.cuenta_proveedor?.nombre && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            color: "var(--text-secondary)",
                            marginTop: 1,
                          }}
                        >
                          {nc.factura_cxp.cuenta_proveedor.nombre}
                        </span>
                      )}
                    </td>
                    <td>{t(MOTIVO_LABELS[nc.motivo] ?? "notasCredito.motivoOtro")}</td>
                    <td>{fmtDate(nc.fecha)}</td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "Inter", fontWeight: 300 }}>
                        {nc.moneda === "USD" ? (
                          <span style={{ color: "#60a5fa" }}>
                            ${Number(nc.monto).toFixed(2)}
                          </span>
                        ) : (
                          <>₡{formatCurrency(nc.monto)}</>
                        )}
                      </div>
                    </td>
                    {isContador && (
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => setConfirmando(nc)}
                          disabled={deletingId === nc.id}
                          title={t("common.eliminar")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            padding: 0,
                            background: "rgba(248,113,113,0.1)",
                            border: "1px solid rgba(248,113,113,0.3)",
                            borderRadius: 6,
                            color: "#f87171",
                            fontSize: "0.85rem",
                            cursor: deletingId === nc.id ? "not-allowed" : "pointer",
                          }}
                        >
                          {deletingId === nc.id ? (
                            <i className="fa-solid fa-circle-notch fa-spin" />
                          ) : (
                            <i className="fa-solid fa-trash" />
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("common.resultados")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{totalPages}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button
              disabled={!previous || loading}
              onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}
            >
              {t("common.anterior")}
            </button>
            <button
              disabled={!next || loading}
              onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}
            >
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {confirmando && (
        <ConfirmModal
          title={t("notasCredito.confirmarEliminarTitulo")}
          message={t("notasCredito.confirmarEliminar", { numero: confirmando.numero_nota })}
          loading={deletingId === confirmando.id}
          onConfirm={confirmarEliminar}
          onCancel={() => setConfirmando(null)}
        />
      )}

      {/* Toast flotante */}
      {actionMsg && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 2000,
            background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
            border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
            borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
            borderRadius: 8,
            padding: "12px 18px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            maxWidth: 380,
          }}
        >
          <i
            className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }}
          />
          <span
            style={{
              color: actionMsg.ok ? "#4ade80" : "#f87171",
              fontWeight: 600,
              fontSize: "0.88rem",
            }}
          >
            {actionMsg.text}
          </span>
        </div>
      )}
    </>
  );
}
